/**
 * Reception tokens are opaque server-issued credentials stored in Firestore.
 * Creation and verification must go through Cloud Functions — never HMAC on the client.
 */
import { RECEPTION_TOKEN_TTL_SECONDS } from "@ambii-com/core";

export { RECEPTION_TOKEN_TTL_SECONDS };

export type CreateReceptionTokenResult = {
  token: string;
  exp: number;
  appointmentId: string;
};

export async function createReceptionTokenViaCallable(
  callable: (data: {
    appointmentId: string;
    ttlSeconds?: number;
  }) => Promise<{ data: CreateReceptionTokenResult }>,
  appointmentId: string,
  ttlSeconds: number = RECEPTION_TOKEN_TTL_SECONDS
): Promise<CreateReceptionTokenResult> {
  const result = await callable({ appointmentId, ttlSeconds });
  return result.data;
}

export async function verifyReceptionAccessViaCallable(
  callable: (data: {
    appointmentId: string;
    token: string;
  }) => Promise<{ data: { valid: boolean } }>,
  appointmentId: string,
  token: string
): Promise<boolean> {
  const result = await callable({ appointmentId, token });
  return !!result.data?.valid;
}

/** Redacted board item from getReceptionQueueBoard (no PII fields). */
export type ReceptionQueueBoardItem = {
  id: string;
  status: string;
  tokenNumber: string;
  roomNumber: string | null;
  roomDirection: string | null;
  position: number;
  appointmentId: string | null;
  instruction: string | null;
};

export async function getReceptionQueueBoardViaCallable(
  callable: (data: {
    appointmentId: string;
    token?: string;
  }) => Promise<{
    data: {
      clinicId: string;
      appointmentId: string;
      entries: ReceptionQueueBoardItem[];
    };
  }>,
  appointmentId: string,
  token?: string
): Promise<ReceptionQueueBoardItem[]> {
  const payload: { appointmentId: string; token?: string } = { appointmentId };
  if (token) payload.token = token;
  const result = await callable(payload);
  return result.data?.entries || [];
}
